import { groupLayerMarkers } from '../modules/layer-screen-clusters';
import { drillDownTransactionCluster } from '../modules/transaction-drilldown';
import GeoJsonFetchLayer from './GeoJsonFetchLayer';
import { centroidOfGeometry } from '../utils/coords';
import { MarkerGroup, createHtmlMarker } from '../utils/markers';
import { addOrUpdateGeoJsonSource, registerCursorHandlers, removeSourceAndLayers } from '../utils/maplibre-helpers';

function getFeatureCenter(feature) {
    if (!feature || !feature.geometry) {
        return null;
    }

    const center = centroidOfGeometry(feature.geometry);
    return center ? { lat: center[1], lng: center[0] } : null;
}

export default class GunbLayer extends GeoJsonFetchLayer {
    static ID = 'gunb';
    static DESCRIPTION = 'Pozwolenia na budowę z Rejestru Głównego Urzędu Nadzoru Budowlanego';
    static MIN_ZOOM = 0;
    static INDIVIDUAL_ZOOM_THRESHOLD = 14;
    static SOURCE_ID = 'src-gunb';
    static BASE_LAYER_IDS = ['lyr-gunb-fill', 'lyr-gunb-line'];
    static SELECTED_SOURCE_ID = 'src-selected-gunb';
    static SELECTED_LAYER_IDS = ['lyr-selected-gunb-fill', 'lyr-selected-gunb-halo', 'lyr-selected-gunb-line'];
    static RENDER_ORDER_LAYER_IDS = GunbLayer.BASE_LAYER_IDS.concat(GunbLayer.SELECTED_LAYER_IDS);
    static BASE_STYLE = {
        fill: {
            'fill-color': 'rgba(124,58,237,0.18)',
            'fill-opacity': 1
        },
        line: {
            'line-color': 'rgba(124,58,237,0.55)',
            'line-width': 2
        }
    };
    static SELECTED_STYLE = {
        fill: {
            'fill-color': 'rgba(124,58,237,0.2)',
            'fill-opacity': 1
        },
        halo: {
            'line-color': 'rgba(255,255,255,0.94)',
            'line-width': 6
        },
        line: {
            'line-color': '#7c3aed',
            'line-width': 3
        }
    };
    static AGGREGATED_STYLE = {
        background: 'rgba(124,58,237,0.82)'
    };

    constructor(config = {}) {
        super(config);
        this.featureGroups = {};
        this.interactionsBound = false;
    }

    getSourceId() {
        return GunbLayer.SOURCE_ID;
    }

    getLayerIds() {
        return GunbLayer.BASE_LAYER_IDS;
    }

    getRenderOrderLayerIds() {
        return GunbLayer.RENDER_ORDER_LAYER_IDS;
    }

    buildUrl() {
        var query = 'bbox=' + this.config.getCurrentBbox()
            + '&zoom=' + this.config.getCurrentZoom();
        var filterQuery = this.config.getFilterQueryString ? this.config.getFilterQueryString() : '';
        if (filterQuery) {
            query += '&' + filterQuery;
        }

        return this.config.baseUrl + 'api/gunb-rwdz?' + query;
    }

    ensureInteractionHandlers() {
        const map = this.getMap();
        if (!map || this.interactionsBound) {
            return;
        }

        GunbLayer.BASE_LAYER_IDS.forEach((layerId) => {
            if (map.getLayer(layerId)) {
                registerCursorHandlers(map, layerId);
            }
        });

        this.interactionsBound = true;
    }

    // Pure grouping of features by parcel (or solo). Does not mutate state. The key is the
    // EGiB identifier of the parcel a permit is drawn on (`parcel_identifier`): the register
    // may write one parcel with and without its map sheet.
    groupFeatures(features) {
        const groups = {};

        (Array.isArray(features) ? features : []).forEach((feature) => {
            const props = feature && feature.properties ? feature.properties : {};
            const key = props.parcel_identifier || props.id_dzialki || ('_solo_' + props.id);

            if (!groups[key]) {
                groups[key] = [];
            }

            groups[key].push(feature);
        });

        return groups;
    }

    getFilterFingerprint() {
        return this.fingerprintFromQuery(
            this.config.getFilterQueryString ? this.config.getFilterQueryString() : ''
        );
    }

    _addBaseLayers(map, sourceId) {
        map.addLayer({
            id: GunbLayer.BASE_LAYER_IDS[0],
            type: 'fill',
            source: sourceId,
            paint: GunbLayer.BASE_STYLE.fill
        });
        map.addLayer({
            id: GunbLayer.BASE_LAYER_IDS[1],
            type: 'line',
            source: sourceId,
            paint: GunbLayer.BASE_STYLE.line
        });
        this.ensureInteractionHandlers();
    }

    renderAggregatedData(data) {
        const map = this.getMap();
        if (!map) {
            return null;
        }

        const aggGroup = new MarkerGroup();
        const formatAggCount = this.config.formatAggCount || ((value) => String(value));

        groupLayerMarkers(data.features, map, data.aggregated === true).forEach((feature) => {
            if (!feature || !feature.geometry || feature.geometry.type !== 'Point') {
                return;
            }

            const props = feature.properties || {};
            const count = Number(props.count || 1);
            const size = count < 10 ? 10 : count < 100 ? 13 : count < 1000 ? 16 : 19;
            const diameter = size * 2;
            const coords = feature.geometry.coordinates;

            aggGroup.add(createHtmlMarker(
                coords,
                '<div class="nm-agg-dot" style="width:' + diameter + 'px;height:' + diameter + 'px;background:' + GunbLayer.AGGREGATED_STYLE.background + '"><span class="nm-agg-count">' + formatAggCount(count) + '</span></div>',
                'nm-agg-wrap',
                {
                    onClick: () => {
                        drillDownTransactionCluster(map, feature);
                    }
                }
            ));
        });

        aggGroup.addTo(map);
        return aggGroup;
    }

    clearSelectedFeature() {
        const map = this.getMap();
        if (!map) {
            return;
        }

        removeSourceAndLayers(map, GunbLayer.SELECTED_SOURCE_ID, GunbLayer.SELECTED_LAYER_IDS);
    }

    renderSelectedFeature(feature) {
        const map = this.getMap();
        if (!map || !feature || !feature.geometry) {
            this.clearSelectedFeature();
            return null;
        }

        this.clearSelectedFeature();

        addOrUpdateGeoJsonSource(map, GunbLayer.SELECTED_SOURCE_ID, {
            type: 'FeatureCollection',
            features: [feature]
        });
        map.addLayer({
            id: GunbLayer.SELECTED_LAYER_IDS[0],
            type: 'fill',
            source: GunbLayer.SELECTED_SOURCE_ID,
            paint: GunbLayer.SELECTED_STYLE.fill
        });
        map.addLayer({
            id: GunbLayer.SELECTED_LAYER_IDS[1],
            type: 'line',
            source: GunbLayer.SELECTED_SOURCE_ID,
            paint: GunbLayer.SELECTED_STYLE.halo
        });
        map.addLayer({
            id: GunbLayer.SELECTED_LAYER_IDS[2],
            type: 'line',
            source: GunbLayer.SELECTED_SOURCE_ID,
            paint: GunbLayer.SELECTED_STYLE.line
        });

        return {
            layer: GunbLayer.SELECTED_SOURCE_ID,
            center: getFeatureCenter(feature)
        };
    }

    findFeatureById(gunbId) {
        if (gunbId == null) {
            return null;
        }

        const targetId = String(gunbId);
        const groupKeys = Object.keys(this.featureGroups);

        for (let groupIndex = 0; groupIndex < groupKeys.length; groupIndex++) {
            const group = this.featureGroups[groupKeys[groupIndex]];
            if (!Array.isArray(group)) {
                continue;
            }

            for (let featureIndex = 0; featureIndex < group.length; featureIndex++) {
                const feature = group[featureIndex];
                const props = feature && feature.properties ? feature.properties : null;
                if (props && String(props.id) === targetId) {
                    return feature;
                }
            }
        }

        return null;
    }

    syncSelectedFeature(gunbId) {
        if (!gunbId || !this.config.getLayer || !this.config.getLayer()) {
            this.clearSelectedFeature();
            return null;
        }

        const feature = this.findFeatureById(gunbId);
        if (!feature) {
            this.clearSelectedFeature();
            return null;
        }

        return this.renderSelectedFeature(feature);
    }

    renderData(data) {
        const map = this.getMap();
        if (!map) {
            return null;
        }

        const isAggregated = data && data.aggregated === true;
        const hasFeatures = data && Array.isArray(data.features) && data.features.length > 0;
        // Keep only the current response: accumulating several dense windows
        // would bypass the server's geometry budget.
        this.resetAccumulator();
        const canAccumulate = !!this.accumulator
            && !isAggregated
            && !!data
            && Array.isArray(data.features);

        // Viewport groups feed the side list, which stays viewport-scoped.
        const viewportGroups = (isAggregated || !hasFeatures)
            ? {}
            : this.groupFeatures(data.features);

        const dropPreviousLayer = () => {
            const previous = this.config.getLayer ? this.config.getLayer() : null;
            if (previous && typeof previous.remove === 'function') {
                previous.remove();
            }
        };

        if (!hasFeatures && !canAccumulate) {
            dropPreviousLayer();
            removeSourceAndLayers(map, this.getSourceId(), this.getLayerIds());
            this.resetAccumulator();
            this.clearSelectedFeature();
            this.featureGroups = {};
            if (this.config.setLayer) {
                this.config.setLayer(null);
            }
            if (this.config.onGroupsChange) {
                this.config.onGroupsChange(viewportGroups, this);
            }
            if (this.config.onRendered) {
                this.config.onRendered(data, null, this);
            }
            return null;
        }

        let layer = null;

        if (isAggregated || !this.accumulator) {
            // Legacy full replace (cluster zoom levels, or accumulation off).
            this.resetAccumulator();
            dropPreviousLayer();
            removeSourceAndLayers(map, this.getSourceId(), this.getLayerIds());

            if (isAggregated) {
                this.clearSelectedFeature();
                this.featureGroups = {};
                layer = this.renderAggregatedData(data);
            } else {
                this.featureGroups = viewportGroups;
                addOrUpdateGeoJsonSource(map, this.getSourceId(), data);
                this._addBaseLayers(map, this.getSourceId());
                layer = this.getSourceId();
            }
        } else {
            // Individual accumulating render. Drop any leftover aggregated
            // marker group, merge into the persistent GeoJSON source.
            const previous = this.config.getLayer ? this.config.getLayer() : null;
            if (previous && previous !== this.getSourceId() && typeof previous.remove === 'function') {
                previous.remove();
            }
            const acc = this.accumulate(data);
            const collection = acc ? acc.collection : data;
            // featureGroups is built from the full accumulated set so map-side
            // selection sync keeps working after the user pans away.
            this.featureGroups = this.groupFeatures(collection.features);

            if (acc && map.getSource(this.getSourceId())) {
                map.getSource(this.getSourceId()).setData(collection);
            } else {
                removeSourceAndLayers(map, this.getSourceId(), this.getLayerIds());
                addOrUpdateGeoJsonSource(map, this.getSourceId(), collection);
                this._addBaseLayers(map, this.getSourceId());
            }
            layer = this.getSourceId();
        }

        if (this.config.setLayer) {
            this.config.setLayer(layer);
        }
        if (this.config.onGroupsChange) {
            this.config.onGroupsChange(viewportGroups, this);
        }
        if (this.config.onRendered) {
            this.config.onRendered(data, layer, this);
        }

        return layer;
    }

    clear() {
        this.abortPending();
        this.resetAccumulator();

        const map = this.getMap();
        const layer = this.config.getLayer ? this.config.getLayer() : null;
        if (layer && typeof layer.remove === 'function') {
            layer.remove();
        }
        if (map) {
            removeSourceAndLayers(map, this.getSourceId(), this.getLayerIds());
        }

        this.clearSelectedFeature();
        this.featureGroups = {};

        if (this.config.setLayer) {
            this.config.setLayer(null);
        }

        if (this.config.onCleared) {
            this.config.onCleared(this);
        }
    }
}
